import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";
import type Session from "users/__session/session";

type SerializeSessionQueryArgs = <QueryArgs extends { session: Session }>(
  params: Omit<Parameters<typeof defaultSerializeQueryArgs>[0], "queryArgs"> & { queryArgs: QueryArgs },
) => string;

/**
 * Cache keys for queries that are handed the session but whose request does not read it.
 *
 * RTK's default key serializes the whole argument, and `Session.toJSON()` carries the account view,
 * the auth token and the open entity agreements. So every change to the session gave these queries
 * a new key and RTK fetched them again: a vendor team member who opened an agreement in the client
 * view saw the agreement go pending a second time when the page moved them to the vendor view
 * (#5698). Worse, an upsert built from `Session.loadFromStorage` — the Ably middleware and the list
 * hydration — reached the mounted entry only while the page's session serialized exactly like the
 * stored one, and otherwise wrote an entry nothing read.
 *
 * Leaving the session out is safe because it never separated one person's data from the next
 * person's: a change of principal clears the whole cache (`principalChanged` →
 * `api.util.resetApiState()`).
 */
export const serializeQueryArgsWithoutSession: SerializeSessionQueryArgs = ({
  queryArgs,
  endpointDefinition,
  endpointName,
}) => {
  const { session: _session, ...requestArgs } = queryArgs;
  return defaultSerializeQueryArgs({ queryArgs: requestArgs, endpointDefinition, endpointName });
};

/**
 * Cache keys for queries whose request reads only the account view from the session.
 *
 * The dashboard counts and the active agreement list send the view as their `context` parameter,
 * so a view switch asks the server a different question and must be a new key. Nothing else on the
 * session reaches these requests. Under the default key a new token, or a change to the open entity
 * agreements from `Session.refreshFromUserInfo`, gave them a new key too, and RTK asked the same
 * question again (#5722). The user stays out of the key for the reason
 * `serializeQueryArgsWithoutSession` gives: a change of principal clears the whole cache.
 */
export const serializeQueryArgsBySessionView: SerializeSessionQueryArgs = ({
  queryArgs,
  endpointDefinition,
  endpointName,
}) => {
  const { session, ...requestArgs } = queryArgs;
  return defaultSerializeQueryArgs({
    queryArgs: { ...requestArgs, accountType: session.accountType },
    endpointDefinition,
    endpointName,
  });
};

/**
 * Cache keys for `users/me` queries, which answer for whoever the bearer token names.
 *
 * The credential is the question these requests ask, so a new credential is a new key and a view
 * switch is not. The credential has to stay in the key: accepting an entity agreement issues a new
 * token for the same user, and `SessionProvider` writes the `users/me` answer it holds back onto the
 * session. Keyed on the user alone, it would write the answer from before the acceptance over the
 * session the acceptance had just established.
 */
export const serializeQueryArgsBySessionCredential: SerializeSessionQueryArgs = ({
  queryArgs,
  endpointDefinition,
  endpointName,
}) => {
  const { session, ...requestArgs } = queryArgs;
  return defaultSerializeQueryArgs({
    queryArgs: {
      ...requestArgs,
      userId: session.user?.id?.value ?? null,
      authToken: session.authToken?.value ?? null,
    },
    endpointDefinition,
    endpointName,
  });
};
